import Info from '@mui/icons-material/Info';
import { Dialog, DialogContent } from '@mui/material';
import { getSGRWarrantyRequests } from 'lib/api/sgr';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import WarrantiesTable from 'lib/common/Tables/WarrantiesTable';
import { SGR_STATES_FILTERS } from 'lib/consts';
import { SGR_TYPES } from 'lib/consts';
import { CustomButtonProps } from 'lib/designSystem/HeaderWithActions';
import Title from 'lib/designSystem/Title';
import CustomToggleButtonGroup from 'lib/designSystem/ToggleButtonGroup';
import { errorHandler } from 'lib/functions/errors';
import { Warranty } from 'lib/types/models';
import React, { useMemo } from 'react';
import { useState } from 'react';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'services/redux/store';

import WarrantyDetailDialog from './WarrantyDetailDialog';
import WarrantyForm from './WarrantyForm';

export const BASE_PAGINATION = {
  page: 1,
  pageSize: 25,
  total: 0,
  totalPages: 0,
};

type StateFilter = (typeof SGR_STATES_FILTERS)[number]['value'];

const Warranties = () => {
  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth);
  const businessIdentifier = auth.user?.businessIdentifier;
  const [stateFilter, setStateFilter] = useState<StateFilter>('active');
  const [pagination, setPagination] = useState(BASE_PAGINATION);
  const [isLoading, setIsLoading] = useState(false);
  const [warranties, setWarranties] = useState<Warranty[]>([]);
  const [openNewQuotation, setOpenNewQuotation] = useState(false);
  const [openWarrantyDetail, setOpenWarrantyDetail] = useState(false);
  const [warranty, setWarranty] = useState<Warranty | null>(null);

  // Filter states
  const [requestId, setRequestId] = useState<number | null>(null);
  const [principalRut, setPrincipalRut] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [type, setType] = useState<string | null>(null);

  const stateFilterOptions = useMemo(() => {
    return SGR_STATES_FILTERS.filter(
      (filter) => filter.value !== 'lost' && filter.value !== 'cancelled',
    );
  }, []);

  const generateQuery = (params?: { page: number; pageSize: number }) => {
    const query = new URLSearchParams();
    const { page = pagination.page } = params ?? {};
    const { pageSize = pagination.pageSize } = params ?? {};
    query.append('page', page.toString());
    query.append('pageSize', pageSize.toString());
    query.append('businessIdentifier', businessIdentifier!);
    query.append('pruffConnect', 'true');
    if (sortBy) query.append('sortBy', sortBy);
    if (sortOrder) query.append('sortOrder', sortOrder);
    if (principalRut) query.append('principalRut', principalRut);
    if (requestId) query.append('warrantyRequestId', requestId.toString());
    if (type) query.append('type', type);
    if (stateFilter) {
      const filter = SGR_STATES_FILTERS.find(
        (filter) => filter.value === stateFilter,
      );
      if (filter) query.append('status', filter.states.join(','));
    }
    return query.toString();
  };

  const fetchWarranties = async (params?: {
    page: number;
    pageSize: number;
  }) => {
    try {
      setIsLoading(true);
      if (!businessIdentifier) return;
      const query = generateQuery(params);
      const response = await getSGRWarrantyRequests(dispatch, query);
      setWarranties(response.data);
      setPagination(response.pagination);
      if (response && warranty) {
        const updatedWarranty = response.data.find(
          (w: Warranty) => w.id === warranty.id,
        );
        if (updatedWarranty) setWarranty(updatedWarranty);
        else setWarranty(null);
      }
      return response;
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setIsLoading(false);
    }
  };

  const successFn = () => {
    setOpenNewQuotation(false);
    setPagination(BASE_PAGINATION);
    void fetchWarranties();
  };

  const handleStateFilterChange = (value: string) => {
    setStateFilter(value as StateFilter);
  };

  const handleOpenWarranty = (warrantyId: number) => {
    const warranty = warranties.find((warranty) => warranty.id === warrantyId);
    if (!warranty) return;
    setWarranty(warranty);
    setOpenWarrantyDetail(true);
  };

  const filters: Filter[] = [
    {
      type: 'text',
      label: 'ID solicitud',
      value: requestId,
      setter: setRequestId,
    },
    {
      type: 'autocomplete',
      label: 'Rut mandante',
      value: principalRut,
      key: 'principalRut',
      setter: setPrincipalRut,
    },
    {
      type: 'select',
      label: 'Tipo',
      value: type,
      setter: setType,
      options: [{ label: 'Todos', value: '' }, ...SGR_TYPES],
    },
  ];

  const buttons: CustomButtonProps[] = [
    {
      name: 'Buscar',
      color: 'primary',
      action: () => {},
      customButton: (
        <GlobalSearcher
          filters={filters}
          triggerSearch={fetchWarranties}
          selectedSortBy={sortBy}
          setSelectedSortBy={setSortBy}
          sortOptions={[{ value: 'createdAt', label: 'Creación' }]}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
          dispatch={dispatch}
        />
      ),
    },
    {
      name: 'Nueva Garantía',
      color: 'primary',
      action: () => setOpenNewQuotation(true),
    },
  ];

  useEffect(() => {
    void fetchWarranties();
  }, [stateFilter]);

  return (
    <>
      <WarrantiesTable
        warranties={warranties}
        setWarranties={setWarranties}
        handleOpenWarrantyDetail={handleOpenWarranty}
        loading={isLoading}
        pagination={pagination}
        setPagination={setPagination}
        fetchWarranties={fetchWarranties}
        setIsLoading={setIsLoading}
        buttons={buttons}
        stateFilter={stateFilter}
        pruffConnect={true}
        stateFilterComponent={
          <CustomToggleButtonGroup
            options={stateFilterOptions}
            value={stateFilter}
            onChange={handleStateFilterChange}
          />
        }
      />
      <Dialog
        open={openNewQuotation}
        onClose={() => setOpenNewQuotation(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          <Title
            title="Nueva Cotización de Garantía"
            icon={<Info color="primary" />}
            color="primary"
          />
          <WarrantyForm direction="column" successFn={successFn} />
        </DialogContent>
      </Dialog>
      <WarrantyDetailDialog
        open={openWarrantyDetail}
        onClose={() => setOpenWarrantyDetail(false)}
        warranty={warranty}
        refreshWarranties={() => fetchWarranties()}
      />
    </>
  );
};

export default Warranties;
